// Shared helpers for technical views: API calls, level names, run styling.
import { url, tokenRef } from "api";

export const BELOW = "below";
export const ROOF = "roof";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

export async function viewsApi(projectId, path = "", { method = "GET", body } = {}) {
  const r = await fetch(`${url}/projects/${projectId}/glance/views${path}`, {
    method,
    headers: { ...(body ? { "Content-Type": "application/json" } : {}), ...auth() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(out.err || `HTTP ${r.status}`);
    err.status = r.status;
    err.body = out;
    throw err;
  }
  return out;
}

// The glance routes themselves (the system mapping lives beside the views,
// not under them).
export async function glanceApi(projectId, path = "", { method = "GET", body } = {}) {
  const r = await fetch(`${url}/projects/${projectId}/glance${path}`, {
    method,
    headers: { ...(body ? { "Content-Type": "application/json" } : {}), ...auth() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(out.err || `HTTP ${r.status}`);
    err.status = r.status;
    err.body = out;
    throw err;
  }
  return out;
}

// `rowIds` (optional) exports only the rows on screen - the page's filter drives exports too.
export const exportQuery = (version, rowIds) => {
  const q = [version ? `version=${version}` : "", rowIds ? `rows=${rowIds.map(encodeURIComponent).join(",")}` : ""].filter(Boolean).join("&");
  return q ? `?${q}` : "";
};
export async function downloadCsv(projectId, viewId, version, rowIds) {
  const r = await fetch(`${url}/projects/${projectId}/glance/views/${encodeURIComponent(viewId)}/export.csv${exportQuery(version, rowIds)}`, { headers: auth() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${viewId}${version ? `-v${version}` : ""}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function levelName(manifest, id) {
  if (id === BELOW) return "Below slab";
  if (id === ROOF) return "Roof";
  if (id == null) return "Not located";
  const l = ((manifest && manifest.levels) || []).find((x) => x.id === id);
  return (l && l.name) || id;
}
export const rowLevel = (manifest, r) => (r.level2 ? `${levelName(manifest, r.level)} to ${levelName(manifest, r.level2)}` : levelName(manifest, r.level));

// The reference register's language: orange sleeves, red security
// conduit, brown below the slab; anything else by system colour.
export function runColor(r) {
  const t = String(r.type || "");
  if (/under\s?ground|below slab/i.test(t)) return "var(--under)";
  if (/security/i.test(t)) return "var(--sec)";
  if (/sleeve|ez-?path/i.test(t)) return "var(--sleeve)";
  if (/DAS/i.test(t)) return "var(--systemink)";
  if (r.system) return `var(--${{ telecom: "tel", power: "pwr", lighting: "ltg", sprinklers: "fire", ducts: "duct", plumbing: "pipe", underfloor: "under", roof: "ink2" }[r.system] || "ink"})`;
  return "var(--ink)";
}
export const runWidth = (r) => (/sleeve/i.test(r.type) ? (r.qty > 1 ? 5 : 3.6) : /backbone|under\s?ground|riser/i.test(r.type) ? 4 : 2.4);
export const runDash = (r) => (/homerun/i.test(r.type) ? "7 4" : /under\s?ground/i.test(r.type) ? "5 4" : undefined);

export const BASIS_LABEL = { model: "Revit model", traced: "Measured from the traced graphic", estimate: "Estimate", assumed: "Assumed", none: "Not measured" };
export const STATUS_LABEL = { draft: "Draft", in_review: "In review", published: "Published" };

export const fmtFt = (v) => (v == null ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }));
